import React, { useState, useEffect } from "react";
import {
  IconUpload,
  IconCheck,
  IconAlertCircle,
  IconAlertTriangle,
  IconDeviceFloppy,
  IconFileSpreadsheet,
  IconArrowRight,
  IconArrowLeft,
  IconRefresh,
} from "@tabler/icons-react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useWarehouseListData } from "../../apis/warehouseApi";
import {
  useParseCustomImportMutation,
  usePreviewCustomImportMutation,
  useExecuteCustomImportMutation,
  useFetchImportTemplates,
  useSaveImportTemplateMutation,
} from "../../apis/customImportApis";

interface CustomOrderImportWizardProps {
  onClose: () => void;
  onSuccess?: () => void;
}

const TARGET_FIELDS = [
  // --- Shipment / Order Details ---
  { value: "orderRef", label: "Order Reference *", required: true },
  { value: "serviceType", label: "Service Type", required: false },
  { value: "cost", label: "Cost (SAR)", required: false },
  { value: "pieces", label: "Pieces Count", required: false },
  { value: "weight", label: "Weight (kg)", required: false },
  { value: "shipmentType", label: "Shipment Type", required: false },
  { value: "paymentMethod", label: "Payment Method", required: false },
  { value: "codAmount", label: "COD Amount (SAR)", required: false },
  { value: "description", label: "Description / Notes", required: false },

  // --- Receiver Details Section ---
  { value: "country", label: "Receiver Country", required: false },
  { value: "customerName", label: "Receiver Name *", required: true },
  { value: "customerPhone", label: "Receiver Mobile *", required: true },
  { value: "customerEmail", label: "Receiver Email", required: false },
  { value: "city", label: "City / Town *", required: true },
  { value: "district", label: "District / Area Name *", required: true },
  { value: "street", label: "Street / Street Number", required: false },
  { value: "building", label: "Building Name / Number", required: false },
  { value: "latitude", label: "Latitude", required: false },
  { value: "longitude", label: "Longitude", required: false },
  { value: "postalCode", label: "Postal Code", required: false },
  { value: "address", label: "Full Address *", required: true },
  { value: "receiverNationalAddress", label: "Short Address", required: false },

  // --- Product Item Details ---
  { value: "sku", label: "Product / SKU Code (FM)", required: false },
  { value: "productName", label: "Product Name", required: false },
  { value: "quantity", label: "Quantity", required: false },
  { value: "unitPrice", label: "Unit Price (SAR)", required: false },
];

const FIELD_CONFIG_MAP: Record<string, { section: "shipment" | "receiver" | "item"; key: string; label: string; type?: "text" | "number" }> = {
  orderRef: { section: "shipment", key: "orderRef", label: "Order Ref" },
  serviceType: { section: "shipment", key: "serviceType", label: "Service Type" },
  cost: { section: "shipment", key: "cost", label: "Cost (SAR)", type: "number" },
  pieces: { section: "shipment", key: "pieces", label: "Pieces Count", type: "number" },
  weight: { section: "shipment", key: "weight", label: "Weight (kg)", type: "number" },
  shipmentType: { section: "shipment", key: "shipmentType", label: "Shipment Type" },
  paymentMethod: { section: "shipment", key: "paymentMethod", label: "Payment Method" },
  codAmount: { section: "shipment", key: "codAmount", label: "COD Amount", type: "number" },
  description: { section: "shipment", key: "description", label: "Description" },

  country: { section: "receiver", key: "country", label: "Receiver Country" },
  customerName: { section: "receiver", key: "customerName", label: "Receiver Name *" },
  customerPhone: { section: "receiver", key: "customerPhone", label: "Receiver Mobile *" },
  customerEmail: { section: "receiver", key: "customerEmail", label: "Receiver Email" },
  city: { section: "receiver", key: "city", label: "City / Town *" },
  district: { section: "receiver", key: "district", label: "District *" },
  street: { section: "receiver", key: "street", label: "Street" },
  building: { section: "receiver", key: "building", label: "Building" },
  latitude: { section: "receiver", key: "latitude", label: "Latitude" },
  longitude: { section: "receiver", key: "longitude", label: "Longitude" },
  postalCode: { section: "receiver", key: "postalCode", label: "Postal Code" },
  address: { section: "receiver", key: "address", label: "Full Address *" },
  receiverNationalAddress: { section: "receiver", key: "nationalAddress", label: "Short Address" },

  sku: { section: "item", key: "sku", label: "SKU" },
  productName: { section: "item", key: "productName", label: "Product Name" },
  quantity: { section: "item", key: "quantity", label: "Qty", type: "number" },
  unitPrice: { section: "item", key: "unitPrice", label: "Unit Price", type: "number" },
};

export default function CustomOrderImportWizard({ onClose, onSuccess }: CustomOrderImportWizardProps) {
  const profile = useSelector((state: any) => state.auth.profile);
  const selectedMerchant = profile?.id || profile?._id || profile?.clientId || "";
  const clientAccess = String(profile?.access || "LM").trim().toUpperCase();

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [customOrderType, setCustomOrderType] = useState<"LM" | "FM">(clientAccess === "FM" ? "FM" : "LM");

  const [selectedWarehouse, setSelectedWarehouse] = useState<string>(profile?.warehouse || "");
  const [file, setFile] = useState<File | null>(null);

  // Default parameters
  const sourceChannel = "Custom Sheet";
  const orderType = "Delivery";
  const fulfillmentType = customOrderType === "FM" ? "First Mile" : "Last Mile";
  const defaultPayment = "COD";

  // Parsed Sheet Data
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [sampleRows, setSampleRows] = useState<Record<string, any>[]>([]);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [detectedConfidence, setDetectedConfidence] = useState<Record<string, number>>({});
  const [templateName, setTemplateName] = useState<string>("");
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState<boolean>(false);

  // Preview & Review State
  const [previewSummary, setPreviewSummary] = useState<any>(null);
  const [activeFilterTab, setActiveFilterTab] = useState<string>("ALL");
  const [editedRows, setEditedRows] = useState<any[]>([]);

  // Execution Progress
  const [isExecuting, setIsExecuting] = useState<boolean>(false);
  const [executionResult, setExecutionResult] = useState<any>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // Data queries
  const { data: warehousesData } = useWarehouseListData();
  const { data: savedTemplates, refetch: refetchTemplates } = useFetchImportTemplates(selectedMerchant);

  // API Mutations
  const { mutate: parseFile, isPending: isParsing } = useParseCustomImportMutation();
  const { mutate: previewFile, isPending: isPreviewing } = usePreviewCustomImportMutation();
  const { mutate: executeImport } = useExecuteCustomImportMutation();
  const { mutate: saveTemplate, isPending: isSavingTemplate } = useSaveImportTemplateMutation();

  const warehousesList = Array.isArray(warehousesData)
    ? warehousesData
    : (warehousesData as any)?.data || [];

  useEffect(() => {
    if (warehousesList.length > 0 && !selectedWarehouse) {
      setSelectedWarehouse(profile?.warehouse || warehousesList[0]?.id || warehousesList[0]?._id || "");
    }
  }, [warehousesList, selectedWarehouse, profile?.warehouse]);

  // Step 1 -> Step 2 Parse Uploaded File
  const handleParseFile = () => {
    if (!selectedMerchant) {
      toast.error("Merchant profile not found. Please re-login.");
      return;
    }
    if (!file) {
      toast.error("Please upload an order file (.xlsx, .xls, .csv)");
      return;
    }

    parseFile(
      { file, merchantId: selectedMerchant, sheetName: selectedSheet },
      {
        onSuccess: (data: any) => {
          if (!data) {
            toast.error("Failed to parse file or no response received.");
            return;
          }
          setSheetNames(data.sheetNames || []);
          setSelectedSheet(data.selectedSheet || "");
          setHeaders(data.headers || []);
          setSampleRows(data.sampleRows || []);

          const initialMapping: Record<string, string> = {};
          const initialConf: Record<string, number> = {};

          (data.detectedMappings || []).forEach((m: any) => {
            initialMapping[m.sourceColumn] = m.detectedField || "";
            initialConf[m.sourceColumn] = m.confidence || 0;
          });

          setFieldMapping(initialMapping);
          setDetectedConfidence(initialConf);

          if (data.suggestedTemplateName) {
            toast.success(`Template matched: "${data.suggestedTemplateName}"`);
          }

          setCurrentStep(2);
        },
        onError: (err: any) => {
          toast.error(err?.message || "Failed to parse uploaded file.");
        },
      }
    );
  };

  // Step 2 -> Step 3 Generate Preview
  const handleGeneratePreview = () => {
    if (!file) return;
    const activeWarehouse = selectedWarehouse || profile?.warehouse || warehousesList[0]?.id || warehousesList[0]?._id || "default_wh";

    previewFile(
      {
        file,
        mappingConfig: fieldMapping,
        merchantId: selectedMerchant,
        warehouseId: activeWarehouse,
        sheetName: selectedSheet,
        defaultPaymentType: defaultPayment,
      },
      {
        onSuccess: (summaryData: any) => {
          setPreviewSummary(summaryData);
          setEditedRows(summaryData.rows || []);
          setCurrentStep(3);
        },
        onError: (err: any) => {
          toast.error(err?.message || "Failed to generate preview.");
        },
      }
    );
  };

  // Save Mapping Template
  const handleSaveTemplate = () => {
    if (!templateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }
    saveTemplate(
      {
        templateName,
        merchantId: selectedMerchant,
        sourceChannel,
        mappingConfig: fieldMapping,
      },
      {
        onSuccess: () => {
          toast.success("Mapping template saved successfully!");
          setShowSaveTemplateModal(false);
          setTemplateName("");
          refetchTemplates();
        },
        onError: (err: any) => {
          toast.error(err?.message || "Failed to save template.");
        },
      }
    );
  };

  // Apply Selected Template
  const handleApplyTemplate = (templateId: string) => {
    const selected = (savedTemplates || []).find((t: any) => t.id === templateId);
    if (!selected || !selected.mappingConfig) return;

    const newMap = { ...fieldMapping, ...selected.mappingConfig };
    setFieldMapping(newMap);
    toast.success(`Applied template "${selected.templateName}"`);
  };

  // Inline Cell Correction for Receiver/Shipment/Item fields
  const handleUpdateRowCell = (rowIndex: number, section: "receiver" | "shipment" | "item", fieldName: string, value: any) => {
    const updated = [...editedRows];
    const targetIdx = updated.findIndex((r) => r.rowIndex === rowIndex);
    if (targetIdx !== -1) {
      updated[targetIdx].cleaned[section][fieldName] = value;

      // Re-validate row
      const r = updated[targetIdx].cleaned;
      const dupIssues = (updated[targetIdx].issues || []).filter((iss: string) => iss.includes("Duplicate"));
      const issues: string[] = [...dupIssues];

      if (!r.shipment.orderRef) issues.push("Missing Order Reference");
      if (!r.receiver.customerName) issues.push("Missing Receiver Name");
      if (!r.receiver.customerPhone || r.receiver.customerPhone.length < 9) issues.push("Invalid Mobile");
      if (!r.receiver.city?.trim()) issues.push("Missing City / Town");
      if (!r.receiver.district?.trim()) issues.push("Missing District");
      if (!r.receiver.address?.trim() && !r.receiver.street?.trim()) issues.push("Missing Address");

      updated[targetIdx].issues = issues;
      const hasHardErrors = issues.some((iss: string) => !iss.includes("COD selected"));
      updated[targetIdx].status = issues.length === 0 ? "VALID" : hasHardErrors ? "ERROR" : "WARNING";
      setEditedRows(updated);
    }
  };

  // Step 3 -> Step 4 Execution
  const handleExecuteImport = () => {
    const validRowsToImport = editedRows
      .filter((r) => r.status === "VALID" || r.status === "WARNING")
      .map((r) => r.cleaned);

    if (validRowsToImport.length === 0) {
      toast.error("No valid or corrected rows to import.");
      return;
    }

    const activeWarehouse = selectedWarehouse || profile?.warehouse || warehousesList[0]?.id || warehousesList[0]?._id || "";

    setImportError(null);
    setIsExecuting(true);
    setCurrentStep(4);

    executeImport(
      {
        rows: validRowsToImport,
        merchantId: selectedMerchant,
        warehouseId: activeWarehouse,
        sourceChannel,
        orderType,
        fulfillmentType,
        fileName: file?.name || "custom_order_import.xlsx",
      },
      {
        onSuccess: (res: any) => {
          setIsExecuting(false);
          setExecutionResult(res);
          toast.success("Orders imported successfully!");
          if (onSuccess) onSuccess();
        },
        onError: (err: any) => {
          setIsExecuting(false);
          const errMsg =
            err?.response?.data?.message ||
            err?.data?.message ||
            err?.message ||
            "Import execution failed.";
          setImportError(errMsg);
          setCurrentStep(3); // Keep user on Step 3 so all manual corrections and edits remain intact!
          toast.error(errMsg);
        },
      }
    );
  };

  // Filtered Rows for Preview Tab
  const filteredRows = editedRows.filter((row) => {
    if (activeFilterTab === "VALID") return row.status === "VALID";
    if (activeFilterTab === "WARNING") return row.status === "WARNING";
    if (activeFilterTab === "ERROR") return row.status === "ERROR";
    if (activeFilterTab === "DUPLICATES") return row.isDuplicate;
    return true;
  });

  return (
    <div className="w-full bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-xl border border-[#D2D2D2]/60 overflow-hidden text-[#212121] shadow-sm">
      {/* Header Banner */}
      <div className="bg-[#FCFDF8] border-b border-[#D2D2D2]/50 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-[#FBD037] text-[#212121] rounded-lg">
            <IconFileSpreadsheet size={20} />
          </div>
          <div>
            <h2 className="text-sm font-bold text-[#212121] tracking-tight">
              Smart Raw Sheet & 3PL Order Import
            </h2>
            <p className="text-[11px] text-[#212121]/70">
              Upload any raw Excel/CSV report — auto-detect & map Order, Receiver, Sender & 3PL fields
            </p>
          </div>
        </div>
      </div>

      {/* Stepper Progress Indicator */}
      <div className="bg-[#FAF8EC] border-b border-[#D2D2D2]/40 px-5 py-2.5">
        <div className="flex items-center justify-between max-w-xl mx-auto text-xs font-medium">
          {[
            { step: 1, label: "1. Select Order Type & File" },
            { step: 2, label: "2. Field Mapping" },
            { step: 3, label: "3. Review & Clean" },
            { step: 4, label: "4. Import Summary" },
          ].map((item) => (
            <div
              key={item.step}
              className={`flex items-center gap-1.5 ${
                currentStep === item.step
                  ? "text-[#212121] font-bold"
                  : currentStep > item.step
                  ? "text-emerald-700 font-semibold"
                  : "text-[#212121]/40"
              }`}
            >
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  currentStep === item.step
                    ? "bg-[#FBD037] text-[#212121]"
                    : currentStep > item.step
                    ? "bg-emerald-500 text-white"
                    : "bg-[#E2E8F0] text-slate-500"
                }`}
              >
                {currentStep > item.step ? <IconCheck size={12} /> : item.step}
              </div>
              <span className="text-[11px]">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* STEP 1: ORDER TYPE SELECTION & UPLOAD ONLY */}
      {currentStep === 1 && (
        <div className="p-5 space-y-4">
          {/* Selection for LM vs FM Order Type */}
          <div className="p-3 bg-[#FCFDF8] border border-[#D2D2D2] rounded-md flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold text-[#212121] uppercase tracking-wide">
                Order Type:
              </span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="customOrderType"
                  value="LM"
                  checked={customOrderType === "LM"}
                  onChange={() => setCustomOrderType("LM")}
                  className="accent-[#FBD037]"
                />
                <span className="text-xs font-medium text-[#212121]">LM Orders (Last Mile)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="customOrderType"
                  value="FM"
                  checked={customOrderType === "FM"}
                  onChange={() => setCustomOrderType("FM")}
                  className="accent-[#FBD037]"
                />
                <span className="text-xs font-medium text-[#212121]">FM Orders (First Mile)</span>
              </label>
            </div>

            {profile?.name || profile?.companyName ? (
              <span className="text-[11px] text-slate-600 bg-yellow-100 px-2.5 py-1 rounded font-semibold">
                Client: {profile?.name || profile?.companyName}
              </span>
            ) : null}
          </div>

          <div>
            <label className="block text-xs font-bold text-[#212121] uppercase tracking-wide mb-1.5">
              Upload Order File (.xlsx, .xls, .csv) *
            </label>
            <div className="border-2 border-dashed border-[#D2D2D2] rounded-xl p-6 text-center bg-[#FCFDF8] hover:bg-yellow-50/50 transition cursor-pointer relative">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className="flex flex-col items-center justify-center gap-2 text-[#212121]/70">
                <div className="w-10 h-10 rounded-full bg-[#FBD037]/20 flex items-center justify-center text-[#212121]">
                  <IconUpload size={20} />
                </div>
                {file ? (
                  <div>
                    <p className="text-xs font-bold text-[#212121]">{file.name}</p>
                    <p className="text-[11px] text-emerald-600 font-medium">
                      {(file.size / 1024).toFixed(1)} KB — Ready to process
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs font-semibold text-[#212121]">
                      Click to upload or drag & drop raw spreadsheet
                    </p>
                    <p className="text-[10px] text-[#212121]/50">
                      Supports any raw export format from Shopify, Salla, Zid, Aramex, SMSA, ERP, or Excel
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-3 border-t border-[#D2D2D2]/40">
            <button
              onClick={handleParseFile}
              disabled={isParsing || !file}
              className="bg-[#FBD037] hover:bg-yellow-400 text-[#212121] font-bold px-5 py-2 rounded-lg flex items-center gap-2 shadow-xs transition disabled:opacity-50 text-xs cursor-pointer"
            >
              {isParsing ? (
                <>
                  <IconRefresh className="animate-spin" size={16} />
                  Detecting Columns...
                </>
              ) : (
                <>
                  Next: Column Mapping
                  <IconArrowRight size={16} />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: FIELD MAPPING & TEMPLATES */}
      {currentStep === 2 && (
        <div className="p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-[#FCFDF8] rounded-md border border-[#D2D2D2]">
            {sheetNames.length > 1 && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-[#212121] uppercase">Sheet:</span>
                <select
                  value={selectedSheet}
                  onChange={(e) => {
                    setSelectedSheet(e.target.value);
                    handleParseFile();
                  }}
                  className="bg-white border border-[#D2D2D2] rounded px-2.5 py-1 text-xs font-medium"
                >
                  {sheetNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {savedTemplates && savedTemplates.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-[#212121] uppercase">Saved Template:</span>
                <select
                  onChange={(e) => handleApplyTemplate(e.target.value)}
                  className="bg-white border border-[#D2D2D2] rounded px-2.5 py-1 text-xs font-medium"
                >
                  <option value="">-- Apply Template --</option>
                  {savedTemplates.map((t: any) => (
                    <option key={t.id} value={t.id}>
                      {t.templateName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <button
              onClick={() => setShowSaveTemplateModal(true)}
              className="text-xs font-bold text-[#212121] bg-yellow-200 hover:bg-yellow-300 px-3 py-1 rounded flex items-center gap-1 transition cursor-pointer"
            >
              <IconDeviceFloppy size={14} />
              Save Template
            </button>
          </div>

          {(() => {
            const mappedValues = new Set(Object.values(fieldMapping).filter(Boolean));
            const isDistrictMapped = mappedValues.has("district");
            return (
              !isDistrictMapped && (
                <div className="p-3 bg-amber-50 border border-amber-300 rounded-md text-amber-900 text-xs flex items-center gap-2">
                  <IconAlertTriangle size={18} className="text-amber-600 shrink-0" />
                  <div>
                    <span className="font-bold">Required Field "District / Area Name *" is Unmapped</span>
                    <p className="text-[11px] text-amber-700">
                      No column in your spreadsheet is mapped to District. All rows will require entering District manually in Step 3 before import can proceed.
                    </p>
                  </div>
                </div>
              )
            );
          })()}

          <div className="border border-[#D2D2D2] rounded-md overflow-x-hidden overflow-y-auto bg-white max-h-[380px]">
            <table className="w-full text-left text-xs table-fixed">
              <thead className="bg-[#FAF8EC] sticky top-0 border-b border-[#D2D2D2] text-[#212121] font-bold uppercase z-10">
                <tr>
                  <th className="p-2 w-[23%] truncate">Source Header</th>
                  <th className="p-2 w-[22%] truncate">Sample Value</th>
                  <th className="p-2 w-[41%] truncate">TrackTech Target Field</th>
                  <th className="p-2 w-[14%] text-center truncate">Confidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {headers.map((header) => {
                  const sampleVal = sampleRows[0]?.[header];
                  const confidence = detectedConfidence[header] || 0;
                  const currentMappedField = fieldMapping[header] || "";

                  return (
                    <tr key={header} className="hover:bg-yellow-50/40 transition">
                      <td className="p-2 font-semibold text-[#212121] truncate" title={header}>{header}</td>
                      <td className="p-2 text-slate-500 truncate" title={sampleVal !== undefined ? String(sampleVal) : ""}>
                        {sampleVal !== undefined && sampleVal !== "" ? String(sampleVal) : "-"}
                      </td>
                      <td className="p-2">
                        <select
                          value={currentMappedField}
                          onChange={(e) =>
                            setFieldMapping({ ...fieldMapping, [header]: e.target.value })
                          }
                          className="w-full bg-[#FCFDF8] border border-[#D2D2D2] rounded px-1.5 py-1 text-xs font-medium focus:ring-1 focus:ring-[#FBD037] cursor-pointer truncate"
                        >
                          <option value="">[ Ignore Column ]</option>
                          {TARGET_FIELDS.map((tf) => (
                            <option key={tf.value} value={tf.value}>
                              {tf.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="p-2 text-center whitespace-nowrap">
                        {confidence >= 95 ? (
                          <span className="bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.5 rounded text-[9px] inline-block whitespace-nowrap">
                            {confidence}% Auto
                          </span>
                        ) : confidence >= 80 ? (
                          <span className="bg-blue-100 text-blue-800 font-bold px-1.5 py-0.5 rounded text-[9px] inline-block whitespace-nowrap">
                            {confidence}% AI
                          </span>
                        ) : (
                          <span className="bg-slate-100 text-slate-600 font-bold px-1.5 py-0.5 rounded text-[9px] inline-block whitespace-nowrap">
                            Manual
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center pt-3 border-t border-[#D2D2D2]/40">
            <button
              onClick={() => setCurrentStep(1)}
              className="text-[#212121] hover:underline text-xs font-bold flex items-center gap-1"
            >
              <IconArrowLeft size={16} /> Back
            </button>
            <button
              onClick={handleGeneratePreview}
              disabled={isPreviewing}
              className="bg-[#FBD037] hover:bg-yellow-400 text-[#212121] font-bold px-5 py-2 rounded-lg flex items-center gap-2 shadow-xs transition disabled:opacity-50 text-xs cursor-pointer"
            >
              {isPreviewing ? (
                <>
                  <IconRefresh className="animate-spin" size={16} />
                  Cleaning & Validating...
                </>
              ) : (
                <>
                  Next: Preview & Clean
                  <IconArrowRight size={16} />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: PREVIEW & INTERACTIVE RECEIVER/SHIPMENT/ITEM ERROR CORRECTION */}
      {currentStep === 3 && previewSummary && (
        <div className="p-5 space-y-4">
          {importError && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 rounded-lg flex items-center justify-between text-xs font-semibold shadow-xs">
              <div className="flex items-center gap-2">
                <IconAlertCircle className="text-rose-600 shrink-0" size={18} />
                <span>
                  <strong>Import Failed:</strong> {importError} — Your corrected rows and edits are preserved below. You can adjust and retry importing.
                </span>
              </div>
              <button
                type="button"
                onClick={() => setImportError(null)}
                className="text-rose-500 hover:text-rose-800 font-bold ml-2 p-1 text-sm cursor-pointer"
                title="Dismiss"
              >
                ✕
              </button>
            </div>
          )}
          {(() => {
            const currentValid = editedRows.filter((r) => r.status === "VALID").length;
            const currentWarnings = editedRows.filter((r) => r.status === "WARNING").length;
            const currentErrors = editedRows.filter((r) => r.status === "ERROR").length;
            const currentDuplicates = editedRows.filter((r) => r.isDuplicate).length;

            return (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                  <div className="bg-white border border-[#D2D2D2] p-2.5 rounded-md text-center">
                    <p className="text-[10px] font-bold text-slate-500 uppercase">Total Rows</p>
                    <p className="text-lg font-extrabold text-[#212121]">{editedRows.length}</p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 p-2.5 rounded-md text-center">
                    <p className="text-[10px] font-bold text-emerald-700 uppercase">Valid</p>
                    <p className="text-lg font-extrabold text-emerald-600">{currentValid}</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 p-2.5 rounded-md text-center">
                    <p className="text-[10px] font-bold text-amber-700 uppercase">Warnings</p>
                    <p className="text-lg font-extrabold text-amber-600">{currentWarnings}</p>
                  </div>
                  <div className="bg-rose-50 border border-rose-200 p-2.5 rounded-md text-center">
                    <p className="text-[10px] font-bold text-rose-700 uppercase">Errors</p>
                    <p className="text-lg font-extrabold text-rose-600">{currentErrors}</p>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 p-2.5 rounded-md text-center col-span-2 sm:col-span-1">
                    <p className="text-[10px] font-bold text-purple-700 uppercase">Duplicates</p>
                    <p className="text-lg font-extrabold text-purple-600">{currentDuplicates}</p>
                  </div>
                </div>

                <div className="flex gap-1.5 border-b border-[#D2D2D2] pb-1.5 text-xs font-bold">
                  {["ALL", "VALID", "WARNING", "ERROR", "DUPLICATES"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveFilterTab(tab)}
                      className={`px-2.5 py-1 rounded transition text-xs ${
                        activeFilterTab === tab
                          ? "bg-[#212121] text-white"
                          : "bg-[#FCFDF8] text-[#212121] hover:bg-yellow-100 border border-[#D2D2D2]/50"
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {/* Table dynamically showing mandatory fields + all non-ignored fields mapped in Step 2 */}
                {(() => {
                  const mandatoryFields = ["orderRef", "customerName", "customerPhone", "city", "district", "address"];
                  const mappedFieldKeys = Array.from(new Set(Object.values(fieldMapping).filter(Boolean)));
                  const activeMappedFields = Array.from(new Set([...mandatoryFields, ...mappedFieldKeys]));

                  return (
                    <div className="border border-[#D2D2D2] rounded-md overflow-x-auto max-h-[320px] bg-white">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-[#FAF8EC] sticky top-0 border-b border-[#D2D2D2] text-[#212121] font-bold uppercase z-10">
                          <tr>
                            <th className="p-2 w-8 text-center">#</th>
                            <th className="p-2">Status</th>
                            {activeMappedFields.map((fieldKey) => {
                              const cfg = FIELD_CONFIG_MAP[fieldKey];
                              return (
                                <th key={fieldKey} className="p-2 whitespace-nowrap min-w-[110px]">
                                  {cfg?.label || fieldKey}
                                </th>
                              );
                            })}
                            <th className="p-2">Issues</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {filteredRows.slice(0, 100).map((row) => (
                            <tr key={row.rowIndex} className="hover:bg-yellow-50/30 transition">
                              <td className="p-2 text-center font-bold text-slate-400">{row.rowIndex}</td>
                              <td className="p-2">
                                {row.status === "VALID" ? (
                                  <span className="bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.5 rounded text-[10px] flex items-center gap-1 w-fit">
                                    <IconCheck size={11} /> Valid
                                  </span>
                                ) : row.status === "WARNING" ? (
                                  <span className="bg-amber-100 text-amber-800 font-bold px-1.5 py-0.5 rounded text-[10px] flex items-center gap-1 w-fit">
                                    <IconAlertTriangle size={11} /> Warning
                                  </span>
                                ) : (
                                  <span className="bg-rose-100 text-rose-800 font-bold px-1.5 py-0.5 rounded text-[10px] flex items-center gap-1 w-fit">
                                    <IconAlertCircle size={11} /> Error
                                  </span>
                                )}
                              </td>
                              {activeMappedFields.map((fieldKey) => {
                                const cfg = FIELD_CONFIG_MAP[fieldKey];
                                if (!cfg) return <td key={fieldKey} className="p-2">-</td>;

                                const currentVal = (row.cleaned[cfg.section] as any)?.[cfg.key] ?? "";
                                const isRequiredField = fieldKey === "district" || fieldKey === "city" || fieldKey === "customerName" || fieldKey === "customerPhone" || fieldKey === "orderRef" || fieldKey === "address";
                                const isMissingRequired = isRequiredField && !String(currentVal).trim();

                                return (
                                  <td key={fieldKey} className="p-2 min-w-[110px]">
                                    <input
                                      type={cfg.type === "number" ? "number" : "text"}
                                      value={currentVal}
                                      placeholder={isRequiredField ? `Enter ${cfg.label} *` : ""}
                                      onChange={(e) =>
                                        handleUpdateRowCell(
                                          row.rowIndex,
                                          cfg.section,
                                          cfg.key,
                                          cfg.type === "number" ? (parseFloat(e.target.value) || 0) : e.target.value
                                        )
                                      }
                                      className={`w-full border rounded px-1.5 py-0.5 text-xs ${
                                        isMissingRequired
                                          ? "bg-rose-50 border-rose-400 placeholder:text-rose-400 font-semibold"
                                          : "bg-[#FCFDF8] border-[#D2D2D2]"
                                      }`}
                                    />
                                  </td>
                                );
                              })}
                              <td className="p-2 text-slate-500 text-[10px] whitespace-nowrap min-w-[120px]">
                                {row.issues && row.issues.length > 0 ? (
                                  <span className="text-rose-600 font-medium">{row.issues.join(", ")}</span>
                                ) : (
                                  <span className="text-emerald-600 font-medium">Clean</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                <div className="flex justify-between items-center pt-3 border-t border-[#D2D2D2]/40">
                  <button
                    onClick={() => setCurrentStep(2)}
                    className="text-[#212121] hover:underline text-xs font-bold flex items-center gap-1"
                  >
                    <IconArrowLeft size={16} /> Back to Mapping
                  </button>
                  <button
                    onClick={handleExecuteImport}
                    disabled={currentErrors > 0 || (currentValid + currentWarnings) === 0}
                    className="bg-[#FBD037] hover:bg-yellow-400 text-[#212121] font-bold px-5 py-2 rounded-lg flex items-center gap-2 shadow-xs transition text-xs cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <IconCheck size={16} />
                    {currentErrors > 0
                      ? `Fix ${currentErrors} Error${currentErrors > 1 ? "s" : ""} (District/Mobile) to Import`
                      : `Import ${currentValid + currentWarnings} Valid Orders`}
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* STEP 4: IMPORT PROGRESS & SUMMARY */}
      {currentStep === 4 && (
        <div className="p-8 text-center space-y-4">
          {isExecuting ? (
            <div className="py-8 space-y-3">
              <div className="w-12 h-12 border-4 border-[#FBD037] border-t-transparent rounded-full animate-spin mx-auto" />
              <h3 className="text-base font-bold text-[#212121]">Importing Orders into TrackTech...</h3>
              <p className="text-xs text-slate-500">
                Creating orders, receiver address profiles, product items, and AWBs
              </p>
            </div>
          ) : (
            <div className="max-w-xs mx-auto space-y-4">
              <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                <IconCheck size={28} />
              </div>
              <div>
                <h3 className="text-lg font-extrabold text-[#212121]">Import Completed!</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Orders have been successfully created in TrackTech.
                </p>
              </div>

              {executionResult && (
                <div className="bg-white border border-[#D2D2D2] rounded-md p-3 space-y-1.5 text-xs">
                  <div className="flex justify-between font-medium">
                    <span>Total Processed:</span>
                    <span className="font-bold">{executionResult.total}</span>
                  </div>
                  <div className="flex justify-between text-emerald-600 font-semibold">
                    <span>Successfully Created:</span>
                    <span className="font-bold">{executionResult.successful}</span>
                  </div>
                  <div className="flex justify-between text-rose-600 font-semibold">
                    <span>Failed / Errors:</span>
                    <span className="font-bold">{executionResult.failed}</span>
                  </div>
                </div>
              )}

              <button
                onClick={onClose}
                className="w-full bg-[#FBD037] hover:bg-yellow-400 text-[#212121] font-bold py-2 rounded-lg transition text-xs cursor-pointer"
              >
                Done & Close
              </button>
            </div>
          )}
        </div>
      )}

      {/* SAVE TEMPLATE MODAL */}
      {showSaveTemplateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-[#FAF8EC] rounded-xl border border-[#D2D2D2] p-5 max-w-sm w-full space-y-3 shadow-xl">
            <h3 className="text-xs font-bold text-[#212121] uppercase">Save Mapping Template</h3>
            <input
              type="text"
              placeholder="e.g. Shopify Custom Order Import"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="w-full bg-white border border-[#D2D2D2] rounded-md px-3 py-2 text-sm text-[#212121] focus:ring-1 focus:ring-[#FBD037]"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSaveTemplateModal(false)}
                className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={isSavingTemplate}
                className="bg-[#FBD037] hover:bg-yellow-400 text-[#212121] text-xs font-bold px-4 py-1.5 rounded-md"
              >
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
